/**
 * The tool-call trail shared by both chats: the model rail and Ask Data Studio.
 *
 * One module rather than a copy per chat, because a second copy is how the two chats
 * drift apart. The styles live with it, so a page that renders the trail without the
 * rail still gets them.
 */
import React from 'react';

import './toolTrail.css';

// One tool call inside a turn, as the server narrates it (product label, never a
// raw tool name). Open until its activity_end arrives; the trail shows a live
// duration meanwhile. Mirrors admin's ModelEditorChat ToolRun.
export interface ToolRun {
  id?: string;
  label: string;
  startedAt: number;
  endedAt?: number;
}

// Same duration format as admin's chat: sub-10s with one decimal, whole seconds after.
export function fmtDur(ms: number): string {
  const s = ms / 1000;
  return s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`;
}

// What the turn is doing RIGHT NOW, as one line.
//
// The gap this closes: tools finish in a couple of seconds and the model then spends
// twenty or forty writing the answer, during which every previous signal was static.
// A trail of ticked-off steps and a total timer both say what HAS happened; neither
// says the turn is still alive. And we already know which phase it is in without
// asking anything: an activity is open, or none is and no token has arrived yet,
// which is the model composing.
export function liveStatus(tools: ToolRun[], answered: boolean): string | null {
  if (answered) return null; // the answer streaming in is its own progress signal
  // An OPEN activity already says this, in the trail, with its own running clock. This
  // line repeating the same label underneath it read as two things happening when only
  // one was, and on a long call the pair sat there identical for half a minute.
  //
  // So this line covers only the gap the trail cannot: the stretch after the last
  // activity closes, when the model is composing and every other signal is static.
  const open = tools.some((t) => t.endedAt == null);
  if (open) return null;
  return tools.length > 0 ? 'Writing the answer' : 'Working';
}

// The trail of tool calls for one assistant turn, each with a live/final duration.
export function ToolTrail({ tools, now }: { tools: ToolRun[]; now: number }) {
  return (
    <div className="mcp-tools">
      {tools.map((t, i) => (
        <div key={i} className="mcp-tool">
          {t.endedAt == null ? (
            <span className="mcp-tool-spinner" aria-label="running" />
          ) : (
            <span className="mcp-tool-done">✓</span>
          )}
          <span className="mcp-tool-label">{t.label}</span>
          <span className="mcp-tool-dur">
            {fmtDur((t.endedAt ?? now) - t.startedAt)}
          </span>
        </div>
      ))}
    </div>
  );
}

/** The one line under the trail while the model composes. See `liveStatus`. */
export function LiveStatus({ label }: { label: string }) {
  return (
    <div className="mcp-live">
      <span className="mcp-tool-spinner" aria-hidden />
      <span className="mcp-live-label">{label}</span>
    </div>
  );
}
